import React, { useState, useEffect, useContext, useMemo, useRef, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/authContext.jsx';
import { useEstadoApp } from '../context/EstadoAppContext.jsx';
import { useLocalStorage } from '../hooks/useLocalStorage.js';
import { initiateSocketConnection } from '../service/socketio.js';

const API = '/api';

const ROLES_SEGUIMIENTO = ['admin', 'admin_mejoramientos', 'mejoramientos'];
const ROLES_REVERTIR = ['admin', 'admin_mejoramientos', 'mejoramientos'];

// Pantalla de mejoramientos/admin_mejoramientos/admin: seguimiento de las
// ventanas de 90 días del Área de Mejoramientos. Igual que Seguimiento de
// Eventos: quién resolvió cada ventana, con qué PV y en qué fecha. El rol
// 'mejoramientos' también entra: es el que envía la notificación en el ciclo 1.
export default function AlertasMejoramientosPage() {
    const { token, role, roles, user } = useContext(AuthContext);
    const navigate = useNavigate();
    const { cambiarEstadoApp } = useEstadoApp();
    const { setItem } = useLocalStorage();

    const puedeRevertir = useMemo(() => {
        if (role === 'admin') return true;
        if (Array.isArray(roles) && roles.some((r) => ROLES_REVERTIR.includes(r))) return true;
        if (Array.isArray(user?.roles) && user.roles.some((r) => ROLES_REVERTIR.includes(r))) return true;
        return ROLES_REVERTIR.includes(role) || ROLES_REVERTIR.includes(user?.role);
    }, [role, roles, user]);

    const [revirtiendoId, setRevirtiendoId] = useState(null);

    const revertirUltimo = async (alertaId) => {
        if (!window.confirm('¿Revertir el ÚLTIMO evento de esta alerta?\n\nSi este evento abrió el ciclo siguiente, se eliminará ese ciclo y el actual volverá a PENDIENTE SIN RESOLVER.\n\nSolo para testeo/capacitación.')) return;
        setRevirtiendoId(alertaId);
        try {
            const res = await fetch(`${API}/alertas-mejoramientos/${encodeURIComponent(alertaId)}/revertir-ultimo-evento`, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' }
            });
            const json = await res.json();
            if (!res.ok || !json.ok) throw new Error(json.error || 'Error al revertir el último evento');
            await cargar({ silencioso: false });
            setAbierto(null);
        } catch (e) {
            alert(e.message);
        } finally {
            setRevirtiendoId(null);
        }
    };

    const puedoVer = useMemo(() => {
        if (role === 'admin') return true;
        if (Array.isArray(roles) && roles.some((r) => ROLES_SEGUIMIENTO.includes(r))) return true;
        return ROLES_SEGUIMIENTO.includes(role);
    }, [role, roles]);

    const [ventanas, setVentanas] = useState([]);
    const [total, setTotal] = useState(0);
    const [pendientesTotal, setPendientesTotal] = useState(0);
    const [enPlazoTotal, setEnPlazoTotal] = useState(0);
    const [resueltasTotal, setResueltasTotal] = useState(0);
    const [cargando, setCargando] = useState(false);
    const [error, setError] = useState('');

    const [estado, setEstado] = useState('');
    const [obra, setObra] = useState('');
    const [pagina, setPagina] = useState(0);
    const POR_PAGINA = 25;

    const [abierto, setAbierto] = useState(null);
    const [arrayEditando, setArrayEditando] = useState([]);
    const debounceRef = useRef(null);

    const headers = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

    // `silencioso` = refresco en segundo plano (socket/poll): no mostramos el
    // "Cargando…" que reemplaza la tabla, así no parpadea cada 60s.
    const cargar = async ({ obra: obraFiltro, estado: estadoFiltro, page, silencioso = false } = {}) => {
        if (!silencioso) setCargando(true);
        setError('');
        try {
            const params = new URLSearchParams();
            const estadoActual = estadoFiltro ?? estado;
            if (estadoActual) params.set('estado', estadoActual);
            const termino = (obraFiltro ?? obra).trim();
            if (termino) params.set('obra', termino);
            params.set('limite', POR_PAGINA);
            params.set('offset', (page ?? pagina) * POR_PAGINA);

            const res = await fetch(`${API}/alertas-mejoramientos/seguimiento?${params.toString()}`, { headers });
            if (!res.ok) throw new Error('Error al cargar el seguimiento de alertas');
            const data = await res.json();
            setVentanas(data?.data?.ventanas || []);
            setTotal(data?.data?.total || 0);
            setPendientesTotal(data?.data?.pendientesTotal || 0);
            setEnPlazoTotal(data?.data?.enPlazoTotal || 0);
            setResueltasTotal(data?.data?.resueltasTotal || 0);
        } catch (e) {
            setError(e.message);
        }
        if (!silencioso) setCargando(false);
    };

    // Guardamos la última versión de `cargar` para que el handler de socket (que se
    // registra una sola vez por [puedoVer, token]) no quede con la closure del
    // montaje: si no, un refresh en tiempo real ignoraría los filtros/página
    // actuales y el usuario no vería reflejado el cambio en lo que está mirando.
    const cargarRef = useRef(null);
    cargarRef.current = cargar;

    useEffect(() => {
        if (!puedoVer) return;
        cargar();
    }, [puedoVer, estado, pagina]); // eslint-disable-line react-hooks/exhaustive-deps

    // Tiempo real: cuando otro usuario resuelve una alerta, el backend emite
    // 'alertas_mejoramientos_actualizadas' y refrescamos la lista y las métricas
    // (Pendientes/En plazo/Resueltas) sin esperar a que el usuario recargue.
    useEffect(() => {
        if (!puedoVer || !token) return undefined;
        const s = initiateSocketConnection();
        const onActualizada = () => { cargarRef.current?.({ silencioso: true }); };
        const onArrayEditando = (data) => setArrayEditando(Array.isArray(data) ? data : []);
        s.on('alertas_mejoramientos_actualizadas', onActualizada);
        s.on('array_editando', onArrayEditando);
        // Estado inicial de bloqueos, para el chequeo previo al doble click.
        fetch(`${API}/arrayEditando`)
            .then((r) => r.json())
            .then((data) => setArrayEditando(Array.isArray(data) ? data : []))
            .catch(() => setArrayEditando([]));
        return () => {
            s.off('alertas_mejoramientos_actualizadas', onActualizada);
            s.off('array_editando', onArrayEditando);
        };
    }, [puedoVer, token]); // eslint-disable-line react-hooks/exhaustive-deps

    // Respaldo del tiempo real: si un evento de socket se pierde (pestaña en
    // segundo plano, reconexión, navegador sin websocket, etc.), refrescamos
    // igual cada 60s. La burbuja de pendientes ya lo hace; esta página no lo
    // tenía y quedaba congelada hasta que el usuario cambiara un filtro.
    useEffect(() => {
        if (!puedoVer || !token) return undefined;
        const id = setInterval(() => { cargarRef.current?.({ silencioso: true }); }, 60000);
        return () => clearInterval(id);
    }, [puedoVer, token]);

    const buscarObra = () => {
        clearTimeout(debounceRef.current);
        setPagina(0);
        cargar({ page: 0 });
    };

    // Al tipear se filtra solo (400ms) para que no haga falta apretar Buscar.
    const onObraChange = (e) => {
        const val = e.target.value;
        setObra(val);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setPagina(0);
            cargar({ obra: val, page: 0 });
        }, 400);
    };

    const limpiarFiltros = () => {
        clearTimeout(debounceRef.current);
        // El efecto de arriba recarga solo cuando cambian `estado` o `pagina`.
        // Si el único filtro activo era la obra (estado vacío y página 0), al
        // limpiar no cambia ninguna dependencia y la tabla quedaría con el
        // resultado filtrado. En ese caso forzamos la recarga con los filtros
        // vacíos (incluido `estado`, que todavía no se actualizó en el closure).
        if (estado === '' && pagina === 0) {
            cargar({ estado: '', obra: '', page: 0 });
        }
        setEstado('');
        setObra('');
        setPagina(0);
    };

    // Verifica si OTRO usuario (distinto al actual) está editando la obra.
    const isEditingByOtherUser = (obraId) => {
        const safeArray = Array.isArray(arrayEditando) ? arrayEditando : [];
        return safeArray.some((objeto) => {
            if (!objeto || !objeto.id_edit || !objeto.user_edit || !objeto.status) return false;
            const userEditClean = String(objeto.user_edit).replace(/"/g, '');
            return objeto.id_edit === obraId && objeto.status === 'editando' && userEditClean !== user;
        });
    };

    const abrirObra = (codigo) => {
        if (!codigo) return;
        setItem('idPartidoObra', codigo);
        // Si otro usuario está editando, InicioPage entra en solo lectura; si no,
        // en modo edición normal. Antes se guardaba 'true' siempre, y mostraba
        // SOLO LECTURA aunque no hubiera nadie más.
        const otroEdita = isEditingByOtherUser(codigo);
        setItem('isEditingPagos', otroEdita ? 'true' : 'false');
        cambiarEstadoApp('edit_desde_pagos', { obra: codigo, origen: 'alertas_mejoramientos' });
        navigate(`/inicio?id_partido_obra=${encodeURIComponent(codigo)}`);
    };

    const fecha = (iso) => (iso ? new Date(iso).toLocaleString('es-AR') : '—');
    // El backend devuelve "YYYY-MM-DD" para fecha_base/fecha_vencimiento. Si
    // llegara un ISO con hora, se toma solo el día (sin corrimiento por TZ).
    const fechaDia = (dia) => {
        if (!dia) return '—';
        const d = new Date(`${String(dia).slice(0, 10)}T00:00:00`);
        return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('es-AR');
    };
    // Contadores calculados en el backend con CURRENT_DATE (día de cierre = hoy si
    // pendiente, día de resolución si resuelta).
    const transcurridos = (n) => (n == null ? '—' : `${n} días`);
    const restantes = (n) => {
        if (n == null) return '—';
        if (n > 0) return `faltan ${n}`;
        if (n === 0) return 'vence hoy';
        return `vencida hace ${-n}`;
    };

    if (!puedoVer) {
        return (
            <div className="p-8 text-center text-gray-500">
                Acceso denegado. Solo mejoramientos, admin_mejoramientos y admin.
            </div>
        );
    }

    const paginas = Math.ceil(total / POR_PAGINA);

    return (
        <div className="p-4 md:p-6">
            <h1 className="text-xl font-bold mb-1">Alertas de Mejoramientos</h1>
            <p className="text-sm text-gray-500 mb-4">
                Ventanas de 90 días de pagos al 100%. En el ciclo 1 la ventana sigue abierta después de enviar el mail; acá se ve cuándo se notificó y, al resolverse, quién cargó la PV y en qué fecha.
            </p>

            {/* Resumen (totales de todas las páginas) */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                    <div className="text-xs text-gray-500">Ventanas</div>
                    <div className="text-2xl font-bold">{total}</div>
                </div>
                <div className="bg-white dark:bg-gray-800 border border-amber-300 dark:border-amber-700 rounded-lg p-3">
                    <div className="text-xs text-gray-500">Vencidas</div>
                    <div className="text-2xl font-bold text-amber-600">{pendientesTotal}</div>
                </div>
                <div className="bg-white dark:bg-gray-800 border border-blue-300 dark:border-blue-700 rounded-lg p-3">
                    <div className="text-xs text-gray-500">En plazo</div>
                    <div className="text-2xl font-bold text-blue-600">{enPlazoTotal}</div>
                </div>
                <div className="bg-white dark:bg-gray-800 border border-green-300 dark:border-green-700 rounded-lg p-3">
                    <div className="text-xs text-gray-500">Resueltas</div>
                    <div className="text-2xl font-bold text-green-600">{resueltasTotal}</div>
                </div>
            </div>

            {/* Filtros */}
            <div className="flex flex-wrap items-end gap-3 mb-4">
                <div>
                    <label className="block text-xs text-gray-500 mb-1">Situación</label>
                    <select
                        value={estado}
                        onChange={(e) => { setEstado(e.target.value); setPagina(0); }}
                        className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800"
                    >
                        <option value="">Todos</option>
                        <option value="pendiente">Vencida</option>
                        <option value="en_plazo">En plazo</option>
                        <option value="resuelta">Resuelta</option>
                    </select>
                </div>

                <div>
                    <label className="block text-xs text-gray-500 mb-1">Obra</label>
                    <div className="flex gap-1">
                        <input
                            value={obra}
                            onChange={onObraChange}
                            onKeyDown={(e) => { if (e.key === 'Enter') buscarObra(); }}
                            placeholder="025-002"
                            className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 w-28"
                        />
                        <button
                            onClick={buscarObra}
                            className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                            Buscar
                        </button>
                    </div>
                </div>

                <button
                    onClick={limpiarFiltros}
                    className="text-sm text-blue-600 hover:text-blue-800 underline pb-1"
                >
                    Limpiar filtros
                </button>
            </div>

            {error && <div className="mb-3 text-sm text-red-600">{error}</div>}

            {/* Tabla */}
            <div className="overflow-x-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-700 text-left">
                        <tr>
                            <th className="px-3 py-2">Obra</th>
                            <th className="px-3 py-2">Ciclo</th>
                            <th className="px-3 py-2">Mail de notificación</th>
                            <th className="px-3 py-2">Inicio de plazo</th>
                            <th className="px-3 py-2">Vencimiento</th>
                            <th className="px-3 py-2">Transcurridos</th>
                            <th className="px-3 py-2">Restantes</th>
                            <th className="px-3 py-2">Situación</th>
                            <th className="px-3 py-2">NRO PV</th>
                            <th className="px-3 py-2">Resuelto por</th>
                            <th className="px-3 py-2"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {cargando && (
                            <tr><td colSpan={11} className="px-3 py-6 text-center text-gray-500">Cargando…</td></tr>
                        )}
                        {!cargando && ventanas.length === 0 && (
                            <tr><td colSpan={11} className="px-3 py-6 text-center text-gray-500">Sin ventanas para estos filtros</td></tr>
                        )}
                        {!cargando && ventanas.map((v) => (
                            <Fragment key={v.id}>
                                <tr className="border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                    <td
                                        className="px-3 py-2 cursor-pointer hover:underline text-blue-700 dark:text-blue-400 whitespace-nowrap"
                                        onDoubleClick={() => abrirObra(v.codigoObra)}
                                        title="Doble click para abrir la obra"
                                    >
                                        {v.codigoObra}
                                        <span className="block text-xs text-gray-500 max-w-[220px] truncate">{v.nombreObra}</span>
                                    </td>
                                    <td className="px-3 py-2">{v.ciclo}</td>
                                    <td className="px-3 py-2 whitespace-nowrap">
                                        {v.notificacionEnviadaEn ? (
                                            <>
                                                <span className="font-medium">{fecha(v.notificacionEnviadaEn)}</span>
                                                {v.ciclo === 1 && v.estado === 'pendiente' && (
                                                    <span className="block text-[11px] text-amber-600 dark:text-amber-400">
                                                        ciclo 1 sigue abierto
                                                    </span>
                                                )}
                                                {v.prorrogaSolicitada && (
                                                    <span className="block text-[11px] text-blue-600 dark:text-blue-400">
                                                        prórroga solicitada {v.prorrogaSolicitadaEn ? `el ${fechaDia(v.prorrogaSolicitadaEn)}` : ''}
                                                    </span>
                                                )}
                                            </>
                                        ) : (
                                            <span className={v.ciclo === 1 && v.estado === 'pendiente' && v.diasRestantes <= 0 ? 'text-gray-500' : 'text-gray-400'}>
                                                {v.ciclo === 1 && v.estado === 'pendiente'
                                                    ? (v.diasRestantes <= 0 ? 'Pendiente de envío' : 'Se envía al vencer')
                                                    : '—'}
                                            </span>
                                        )}
                                    </td>
                                    <td
                                        className="px-3 py-2 whitespace-nowrap"
                                        title={v.ciclo > 1
                                            ? `Esta ventana nació al cargar el NRO PV del ciclo ${v.ciclo - 1}`
                                            : 'Ciclo 1: día del pago al 100%. Ciclos siguientes: día en que se cargó el NRO PV'}
                                    >
                                        {fechaDia(v.fechaBase)}
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap">{fechaDia(v.fechaVencimiento)}</td>
                                    <td className={`px-3 py-2 whitespace-nowrap ${v.estado === 'resuelta' ? 'text-gray-400' : ''}`}>{transcurridos(v.diasTranscurridos)}</td>
                                    <td className={`px-3 py-2 whitespace-nowrap ${v.estado === 'resuelta' ? 'text-gray-400' : ''}`}>
                                        {v.estado === 'resuelta' ? '—' : restantes(v.diasRestantes)}
                                    </td>
                                    <td className="px-3 py-2">
                                        {v.estado === 'resuelta'
                                            ? <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs">Resuelta</span>
                                            : (v.diasRestantes > 0
                                                ? <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">En plazo</span>
                                                : <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">Vencida</span>)}
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap">
                                        {v.nroPv || '—'}
                                        {v.plazoDias != null && (
                                            <span className="block text-[11px] text-amber-600 dark:text-amber-400">
                                                prórroga {v.plazoDias} días
                                            </span>
                                        )}
                                        {v.estado === 'resuelta' && v.siguienteCiclo != null && (
                                            <span
                                                className="block text-[11px] text-amber-600 dark:text-amber-400 font-medium"
                                                title="Esta resolución abrió la siguiente ventana (ciclo nuevo)"
                                            >
                                                → abre ciclo {v.siguienteCiclo} · inicio {fechaDia(v.siguienteBase)}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap">
                                        {v.estado === 'pendiente' ? '—' : (
                                            <>
                                                <span className="font-medium">{v.resueltoPorNombre || `#${v.resueltoPor}`}</span>
                                                <span className="block text-xs text-gray-400">{fecha(v.resueltoEn)}</span>
                                            </>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <button
                                            onClick={() => setAbierto(abierto === v.id ? null : v.id)}
                                            className="text-xs text-blue-600 hover:text-blue-800"
                                        >
                                            {abierto === v.id ? 'Ocultar' : 'Eventos'}
                                        </button>
                                    </td>
                                </tr>
                                {abierto === v.id && (
                                    <tr className="bg-gray-50 dark:bg-gray-700/40">
                                        <td colSpan={11} className="px-3 py-3">
                                            <div className="text-xs font-semibold text-gray-500 mb-1">
                                                Eventos ({v.eventos.length})
                                            </div>
                                            {v.eventos.length === 0 && <div className="text-sm text-gray-400">Sin eventos</div>}
                                            {[...v.eventos].sort((a,b)=>new Date(b.creado_en)-new Date(a.creado_en)).map((ev, idx) => {
                                                const esUltimo = idx === 0;
                                                const puedePendiente = v.estado === 'pendiente' && !v.siguienteCiclo;
                                                const esSolicitudProrroga = ev.tipo === 'solicitud_prorroga';
                                                const esCierre = ['respuesta_prorroga', 'nro_pv', 'descripcion'].includes(ev.tipo);
                                                const esRevertible =
                                                    esUltimo &&
                                                    puedeRevertir &&
                                                    !revirtiendoId &&
                                                    (
                                                        (esSolicitudProrroga && puedePendiente) ||
                                                        (puedePendiente && ['notificacion', 'reprogramacion'].includes(ev.tipo)) ||
                                                        // Evento de cierre huérfano: quedó 'nro_pv' u otro
                                                        // tras un revert parcial anterior. Se permite
                                                        // limpiarlo aunque la ventana ya esté pendiente.
                                                        (puedePendiente && esCierre) ||
                                                        (v.estado === 'resuelta' && v.siguienteCiclo != null && esCierre)
                                                    );
                                                const idBoton = esSolicitudProrroga ? `prorroga:${v.id}` : v.id;
                                                const etiqueta = esSolicitudProrroga
                                                    ? 'Revertir solicitud de prórroga'
                                                    : v.estado === 'resuelta'
                                                        ? 'Revertir y volver a pendiente'
                                                        : esCierre
                                                            ? 'Revertir evento de cierre'
                                                            : 'Revertir último evento';
                                                return (
                                                    <div key={ev.id} className="text-sm flex items-center justify-between gap-3 py-0.5">
                                                        <div className="flex items-center gap-3 flex-wrap">
                                                            <span className="text-gray-400">
                                                                {ev.tipo === 'solicitud_prorroga' ? fechaDia(ev.fecha) : fecha(ev.creado_en)}
                                                            </span>
                                                            <span className="font-medium">{ev.tipo}</span>
                                                            {ev.nro_pv && <span className="text-gray-600 dark:text-gray-300">PV: {ev.nro_pv}</span>}
                                                            {ev.descripcion && <span className="text-gray-600 dark:text-gray-300">{ev.descripcion}</span>}
                                                            {ev.creado_por && <span className="text-xs text-gray-400">por #{ev.creado_por}</span>}
                                                        </div>
                                                        {esRevertible && (
                                                            <button
                                                                onClick={() => revertirUltimo(idBoton)}
                                                                className="text-xs text-red-600 hover:text-red-800 disabled:opacity-40"
                                                                disabled={revirtiendoId === idBoton}
                                                            >
                                                                {revirtiendoId === idBoton ? 'Revirtiendo...' : etiqueta}
                                                            </button>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </td>
                                    </tr>
                                )}
                            </Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Paginación */}
            {paginas > 1 && (
                <div className="flex items-center justify-center gap-3 mt-4 text-sm">
                    <button
                        onClick={() => setPagina((p) => Math.max(0, p - 1))}
                        disabled={pagina === 0}
                        className="border border-gray-300 dark:border-gray-600 rounded px-3 py-1 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                        Anterior
                    </button>
                    <span>Página {pagina + 1} de {paginas}</span>
                    <button
                        onClick={() => setPagina((p) => Math.min(paginas - 1, p + 1))}
                        disabled={pagina >= paginas - 1}
                        className="border border-gray-300 dark:border-gray-600 rounded px-3 py-1 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                        Siguiente
                    </button>
                </div>
            )}
        </div>
    );
}